export type ScreenBreakpoint = "mobile" | "tablet" | "desktop";

const getBreakpoint = (width: number): ScreenBreakpoint => {
  if (width >= 1024) return "desktop";
  if (width >= 600) return "tablet";
  return "mobile";
};

type TopicSummaryGridBreakpointConfig = {
  horizontalPadding: number;
  columnGap: number;
  minCardWidth: number;
};

const TOPIC_SUMMARY_GRID_CONFIG: Record<
  ScreenBreakpoint,
  TopicSummaryGridBreakpointConfig
> = {
  mobile: { horizontalPadding: 16, columnGap: 16, minCardWidth: 181 },
  tablet: { horizontalPadding: 24, columnGap: 16, minCardWidth: 226 },
  desktop: { horizontalPadding: 32, columnGap: 24, minCardWidth: 181 },
};

export function getTopicSummaryGridConfigForScreenWidth(
  screenWidth: number,
): TopicSummaryGridBreakpointConfig {
  return TOPIC_SUMMARY_GRID_CONFIG[getBreakpoint(screenWidth)];
}

export interface GridLayout {
  numColumns: number;
  cardWidth: number;
  horizontalPadding: number;
  columnGap: number;
}

export interface ResponsiveGridLayoutInput {
  containerContentWidth: number;
  minCardWidth: number;
  columnGap: number;
  horizontalPadding?: number;
  minColumns?: number;
  fitMinCardWidth?: number;
}

export interface ResponsiveGridLayout {
  columns: number;
  cardWidth: number;
  columnGap: number;
  horizontalPadding: number;
  contentWidth: number;
  totalGapWidth: number;
}

export function getMaxGridColumnCount(
  availableWidth: number,
  columnGap: number,
  minCardWidth: number,
): number {
  return Math.max(
    1,
    Math.floor((availableWidth + columnGap) / (minCardWidth + columnGap)),
  );
}

export const getGridLayout = (screenWidth: number): GridLayout => {
  const { horizontalPadding, columnGap, minCardWidth } =
    getTopicSummaryGridConfigForScreenWidth(screenWidth);

  const availableWidth = screenWidth - horizontalPadding * 2;
  const numColumns = getMaxGridColumnCount(
    availableWidth,
    columnGap,
    minCardWidth,
  );
  const cardWidth =
    (availableWidth - (numColumns - 1) * columnGap) / numColumns;

  return { numColumns, cardWidth, horizontalPadding, columnGap };
};

export function getHorizontalCardConfiguration(screenWidth: number): {
  cardWidth: number;
  height: number;
  horizontalPadding: number;
  columnGap: number;
} {
  const height = 176;
  const { horizontalPadding, columnGap, minCardWidth } =
    getTopicSummaryGridConfigForScreenWidth(screenWidth);

  return { cardWidth: minCardWidth, height, horizontalPadding, columnGap };
}

export function getResponsiveGridLayout({
  containerContentWidth,
  minCardWidth,
  columnGap,
  horizontalPadding = 0,
  fitMinCardWidth,
  minColumns = 1,
}: ResponsiveGridLayoutInput): ResponsiveGridLayout {
  const width = Math.max(0, containerContentWidth);
  if (width <= 0) {
    return {
      columns: minColumns,
      cardWidth: minCardWidth,
      columnGap,
      horizontalPadding,
      contentWidth: width,
      totalGapWidth: 0,
    };
  }

  const effectiveFitMinCardWidth = fitMinCardWidth ?? minCardWidth;
  const denseColumns = getMaxGridColumnCount(
    width,
    columnGap,
    effectiveFitMinCardWidth,
  );
  const columns = Math.max(minColumns, denseColumns);
  const totalGapWidth = (columns - 1) * columnGap;
  const cardWidth = (width - totalGapWidth) / columns;

  return {
    columns,
    cardWidth,
    columnGap,
    horizontalPadding,
    contentWidth: width,
    totalGapWidth,
  };
}

export function findLastIndex<T>(
  arr: T[],
  predicate: (item: T) => boolean,
): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (predicate(arr[i])) return i;
  }
  return -1;
}
